/** A headless game loop, so an AI-vs-AI game can be run without a renderer.
 *
 * This is the *driver*, not the AI, and the difference is the point: the driver
 * holds the authoritative `GameState` because somebody has to, and it hands each
 * controller nothing but `observe(state, player)`. I9 is visible in one line
 * here, which is worth more than a paragraph of documentation about it.
 *
 * The loop is bounded three ways - days, commands, and a stall counter - because
 * "the game terminates" has to be a property of the driver, not a hope about the
 * planner. A neutral guard army has no `PlayerId` and therefore no controller;
 * it is driven through `enumerateNeutralBattleCommands`, the door the kernel
 * leaves open for exactly this.
 */

import {
  SIDE_ATTACKER, SIDE_DEFENDER, absoluteDay, activeStack,
  enumerateNeutralBattleCommands, executeCommand, observe, tacticsSide,
} from "@game/core";
import type {
  ContentRegistry, ExecuteError, GameCommand, GameEvent, GameState, PlayerId,
} from "@game/core";
import { chooseBattleCommand } from "./battle.js";
import { resolveDifficulty, type DifficultyProfile } from "./difficulty.js";
import type { PlayerController } from "./types.js";

export interface RejectedCommand {
  readonly command: GameCommand;
  readonly error: ExecuteError;
  readonly player: PlayerId;
  readonly day: number;
}

export type StopReason = "victory" | "maxDays" | "maxCommands" | "stalled";

/**
 * `TurnState.day` counts 1..7 inside a week, so a bound written against it never
 * fires. Everything here counts in absolute days, which is what a caller asking
 * for "two hundred turns" means.
 */
export const dayCount = (s: GameState): number => absoluteDay(s);

/** Commands one player-turn may spend before the driver calls the game hung. */
export const STALL_LIMIT = 4096;

/** A signal that never fires, so a caller who passes none costs nothing. */
const NEVER_ABORTS: AbortSignal = new AbortController().signal;

export interface GameRunResult {
  readonly state: GameState;
  readonly commands: number;
  readonly days: number;
  readonly stopped: StopReason;
  /** Absolute days elapsed, not the 1..7 day-of-week in `TurnState`. */
  /** Empty is the contract: the AI must never issue a command the kernel refuses. */
  readonly rejected: readonly RejectedCommand[];
}

export interface GameRunOptions {
  readonly content: ContentRegistry;
  readonly controllers: Readonly<Record<string, PlayerController>>;
  readonly maxDays?: number;
  readonly maxCommands?: number;
  /** Policy for wandering monsters and other ownerless battle sides. */
  readonly neutral?: DifficultyProfile;
  readonly signal?: AbortSignal;
  readonly onEvent?: (e: GameEvent, state: GameState) => void;
  /** Passed to `executeCommand`; the tests turn it on. */
  readonly verify?: boolean;
}

/** The side of the current battle nobody owns, if it is that side's move. */
function neutralSideToAct(state: GameState): 0 | 1 | null {
  const b = state.battle;
  if (b === null) return null;
  // One side holds the tactics phase (`BattleState.tacticsHexes` is signed), so
  // there is exactly one candidate rather than a search over both.
  if (b.tacticsPhase) {
    const side = tacticsSide(b);
    return b.heroes[side]!.owner === null ? side : null;
  }
  const slot = activeStack(b);
  if (slot < 0) return null;
  const side = (b.stacks.side[slot] === SIDE_DEFENDER ? SIDE_DEFENDER : SIDE_ATTACKER) as 0 | 1;
  return b.heroes[side].owner === null ? side : null;
}

/** Whoever the kernel will accept a command from right now. */
function toAct(state: GameState): PlayerId | null {
  if (state.victory.over) return null;
  if (state.pending !== null) return state.pending.playerId;
  const b = state.battle;
  if (b !== null) {
    if (b.tacticsPhase) return b.heroes[tacticsSide(b)]!.owner;
    const slot = activeStack(b);
    if (slot < 0) return null;
    const side = (b.stacks.side[slot] === SIDE_DEFENDER ? SIDE_DEFENDER : SIDE_ATTACKER) as 0 | 1;
    return b.heroes[side].owner;
  }
  return state.turn.activePlayer;
}

export async function runGame(
  start: GameState, opts: GameRunOptions,
): Promise<GameRunResult> {
  const maxDays = opts.maxDays ?? 200;
  const maxCommands = opts.maxCommands ?? 200_000;
  const neutral = opts.neutral ?? resolveDifficulty("normal");
  const rejected: RejectedCommand[] = [];

  let state = start;
  let commands = 0;
  let stopped: StopReason = "maxCommands";
  // The driver's own safety net. Progress is defined as the turn moving on -
  // a controller that keeps issuing legal commands forever inside one turn is
  // still a hung game, and no bound on `version` would notice.
  let stall = 0;
  let stallKey = "";

  for (;;) {
    if (state.victory.over) { stopped = "victory"; break; }
    if (dayCount(state) > maxDays) { stopped = "maxDays"; break; }
    if (commands >= maxCommands) { stopped = "maxCommands"; break; }
    if (stall > STALL_LIMIT) { stopped = "stalled"; break; }
    if (opts.signal?.aborted === true) { stopped = "maxCommands"; break; }

    const nside = neutralSideToAct(state);
    let cmd: GameCommand | null = null;

    if (nside !== null && state.battle !== null) {
      const legal = enumerateNeutralBattleCommands(state, opts.content);
      const only = legal
        .filter((c) => c.type === "BATTLE")
        .map((c) => (c as Extract<GameCommand, { type: "BATTLE" }>).cmd);
      const pick = chooseBattleCommand(state.battle, nside, opts.content, neutral, only);
      cmd = pick === null ? null : { type: "BATTLE", cmd: pick };
    } else {
      const player = toAct(state);
      if (player === null) { stopped = "stalled"; break; }
      const controller = opts.controllers[player as string];
      if (controller === undefined) { stopped = "stalled"; break; }
      const obs = observe(state, player, opts.content);
      // The caller's signal is handed straight down. Deriving a fresh child per
      // command would attach a listener per command and never remove it.
      cmd = await controller.chooseCommand(obs, opts.signal ?? NEVER_ABORTS);
    }

    if (cmd === null) { stopped = "stalled"; break; }

    const actor = toAct(state) ?? state.turn.activePlayer;
    const r = executeCommand(state, cmd, opts.content, { verify: opts.verify === true });
    if (!r.ok) {
      rejected.push({ command: cmd, error: r.error, player: actor, day: dayCount(state) });
      // A refused command would otherwise spin: force the turn on.
      const forced = executeCommand(
        state, { type: "END_TURN", player: state.turn.activePlayer }, opts.content,
      );
      if (!forced.ok) { stopped = "stalled"; break; }
      state = forced.value.state;
      commands++;
      continue;
    }
    state = r.value.state;
    commands++;
    if (opts.onEvent !== undefined) for (const e of r.value.events) opts.onEvent(e, state);
    const key = `${dayCount(state)}:${state.turn.activePlayer}`;
    if (key === stallKey) stall++;
    else { stallKey = key; stall = 0; }
  }

  return { state, commands, days: dayCount(state), stopped, rejected };
}
